import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const T = {
  en: { verifying: "Verifying address…", verified: "Verified address", outside: "This address is outside our coverage area (Bexar, Comal, Guadalupe, Kendall). You can still continue.", nomatch: "We couldn't verify this address with the U.S. Census Bureau. You can still continue.", unavailable: "Address verification is temporarily unavailable. You can still continue.", attribution: "Powered by Google", listbox: "Address suggestions" },
  es: { verifying: "Verificando dirección…", verified: "Dirección verificada", outside: "Esta dirección está fuera de nuestra área de cobertura (Bexar, Comal, Guadalupe, Kendall). Puede continuar de todos modos.", nomatch: "No pudimos verificar esta dirección con la Oficina del Censo de EE. UU. Puede continuar de todos modos.", unavailable: "La verificación de direcciones no está disponible temporalmente. Puede continuar de todos modos.", attribution: "Powered by Google", listbox: "Sugerencias de dirección" },
};

// ARIA combobox. Debounced 250ms, 3-char minimum. On select → Census verify → onVerified(record|null).
export const AddressAutocomplete = ({ id, label, value, onChange, onVerified, lang = "en", testId = "address-input", required = true }) => {
  const t = T[lang] || T.en;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [fallback, setFallback] = useState(false);
  const [verify, setVerify] = useState({ state: "idle", record: null, reason: "" });
  const timer = useRef(null);
  const skipNext = useRef(false);
  const session = useRef(null);
  const listId = `${id}-listbox`;

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    clearTimeout(timer.current);
    if (!value || value.trim().length < 3 || fallback) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        if (!session.current || Date.now() - session.current.at > 3 * 60 * 1000) session.current = { id: crypto.randomUUID(), at: Date.now() };
        session.current.at = Date.now();
        const r = await fetch(`${API}/address/suggest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ q: value, lang, session_token: session.current.id }) });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (d.fallback) { setFallback(true); setItems([]); setOpen(false); return; }
        setItems(d.suggestions || []); setOpen((d.suggestions || []).length > 0); setActive(-1);
      } catch { setItems([]); setOpen(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [value, lang, fallback]);

  const pick = async (s) => {
    skipNext.current = true;
    session.current = null;
    onChange(s.label);
    setOpen(false); setItems([]);
    setVerify({ state: "verifying", record: null, reason: "" });
    try {
      const r = await fetch(`${API}/address/verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ address: s.label, place_id: s.place_id }) });
      const d = await r.json();
      const rec = r.ok ? d : null;
      setVerify({ state: rec && rec.address_verified ? "verified" : "unverified", record: rec, reason: (rec && rec.reason) || (r.ok ? "" : "census_unavailable") });
      onVerified && onVerified(rec);
    } catch { setVerify({ state: "unverified", record: null, reason: "census_unavailable" }); onVerified && onVerified(null); }
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const msg = verify.state === "verifying" ? t.verifying
    : verify.state === "verified" ? t.verified
    : verify.state === "unverified" ? (verify.reason === "outside_coverage" ? t.outside : verify.reason === "census_unavailable" ? t.unavailable : t.nomatch) : "";

  return (
    <div className="relative">
      <label className="block text-sm font-semibold mb-1" htmlFor={id}>{label}</label>
      <input id={id} required={required} value={value} autoComplete="off" spellCheck={false}
             role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={listId} aria-activedescendant={active >= 0 ? `${id}-opt-${active}` : undefined}
             onChange={e => { if (verify.state !== "idle") { setVerify({ state: "idle", record: null, reason: "" }); onVerified && onVerified(null); } onChange(e.target.value); }}
             onKeyDown={onKey} onBlur={() => setTimeout(() => setOpen(false), 150)} onFocus={() => items.length && setOpen(true)}
             className="w-full border border-slate-300 rounded-md px-3 py-3 text-base min-h-[48px] bg-white" data-testid={testId} />
      {open && (
        <ul id={listId} role="listbox" aria-label={t.listbox} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto" data-testid={`${testId}-listbox`}>
          {items.map((s, i) => (
            <li key={s.place_id || i} id={`${id}-opt-${i}`} role="option" aria-selected={i === active}
                onMouseDown={e => { e.preventDefault(); pick(s); }} onMouseEnter={() => setActive(i)}
                className={`px-3 py-3 text-sm cursor-pointer min-h-[44px] ${i === active ? "bg-slate-100" : ""}`} data-testid={`${testId}-option-${i}`}>
              {s.label}
            </li>
          ))}
          <li role="presentation" className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100 text-right" data-testid={`${testId}-attribution`}>{t.attribution}</li>
        </ul>
      )}
      {msg && (
        <div className={`mt-1 text-xs inline-flex items-start gap-1.5 ${verify.state === "verified" ? "text-emerald-800" : verify.state === "verifying" ? "text-slate-500" : "text-amber-800"}`} data-testid={`${testId}-verify-status`} role="status">
          {verify.state === "verifying" ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0 mt-0.5" /> : verify.state === "verified" ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
          <span>{msg}{verify.state === "verified" && verify.record?.county ? ` · ${verify.record.county} County` : ""}</span>
        </div>
      )}
    </div>
  );
};
